import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { Subject } from 'rxjs';
import {
  MessagePayload,
  Messaging,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';
import { FirebaseAppService } from './firebase-app.service';

declare const ngDevMode: boolean | undefined;

@Injectable({ providedIn: 'root' })
export class FirebaseMessagingService {
  private messaging?: Messaging;

  private readonly messageSubject = new Subject<MessagePayload>();
  readonly messages$ = this.messageSubject.asObservable();

  constructor(
    private readonly firebaseAppService: FirebaseAppService,
    private readonly zone: NgZone,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  async initialize(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const supported = await isSupported().catch(() => false);
    if (!supported) {
      if (typeof ngDevMode !== 'undefined' && ngDevMode) {
        console.warn('[Firebase Messaging] Browser no soporta FCM.');
      }
      return;
    }

    const app = await this.firebaseAppService.getFirebaseApp();
    if (!app) {
      if (typeof ngDevMode !== 'undefined' && ngDevMode) {
        console.warn('[Firebase Messaging] FirebaseApp no disponible.');
      }
      return;
    }

    if (!this.messaging) {
      this.messaging = getMessaging(app);

      onMessage(this.messaging, (payload) => {
        this.zone.run(() => {
          this.messageSubject.next(payload);
        });
      });
    }
  }

  async requestPermissionAndToken(): Promise<string | null> {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    await this.initialize();
    if (!this.messaging) {
      return null;
    }

    if (!('Notification' in window)) {
      if (typeof ngDevMode !== 'undefined' && ngDevMode) {
        console.warn(
          '[Firebase Messaging] Las notificaciones no estan disponibles en este navegador.'
        );
      }
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      if (typeof ngDevMode !== 'undefined' && ngDevMode) {
        console.warn(
          '[Firebase Messaging] El usuario no otorgo permiso de notificaciones.'
        );
      }
      return null;
    }

    const config = this.firebaseAppService.getConfig();
    try {
      const token = await getToken(this.messaging, {
        vapidKey: config?.messaging?.vapidKey,
      });
      return token ?? null;
    } catch (error) {
      console.error(
        '[Firebase Messaging] No se pudo obtener el token FCM.',
        error
      );
      return null;
    }
  }
}
